import React, { useEffect, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import LeadTimeSelector from "../../../components/LeadTimeSelector.jsx";
import { phoneUi } from "../../ui/phoneUi";
import { shopToday } from "../../../utils/shopDate";
import { QUICK_EDIT_FAILURE, promisedEditStart, promisedPayload, propagationNote, saveOrderFields } from "../../../features/salesOrders/soData";

// The order's quick edits on a phone (plan: promised date, target date, PO number). The requests and
// the failure messages are features/salesOrders/soData.js, the desktop page's own.
//   kind 'po' | 'promised' | 'target'; onSaved({header, note}) after the API took it.

const TITLES = { po: "Customer PO", promised: "Promised completion", target: "Target completion (internal)" };

export default function OrderEditPanel({ kind, header, soId, onClose, onSaved }) {
  const [po, setPo] = useState("");
  const [mode, setMode] = useState("lead_time");
  const [leadTime, setLeadTime] = useState("Standard (10-14 Days)");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Start from the order as it is, as the desktop's edit does.
  useEffect(() => {
    if (!kind) return;
    setError(null);
    setPo(header?.customer_po || "");
    if (kind === "promised") {
      const start = promisedEditStart(header);
      setMode(start.mode);
      setLeadTime(start.leadTime);
      setDate(start.date);
    }
    if (kind === "target") setDate(header?.target_completion_date ? header.target_completion_date.slice(0, 10) : "");
  }, [kind, header]);

  const save = async (patch, failure) => {
    setSaving(true);
    setError(null);
    try {
      let note = null;
      const saved = await saveOrderFields({ soId, patch, failure, ui: phoneUi, onSaved: (data) => { note = propagationNote(data); } });
      if (saved.ok) onSaved({ header: saved.header, note });
    } catch (err) {
      setError(err.message || "Couldn't reach JobQ.");
    } finally {
      setSaving(false);
    }
  };

  // ASAP = the shop's (Central) today, as on the desktop.
  const asap = () => save({ target_completion_date: shopToday() }, QUICK_EDIT_FAILURE.target);

  let body = null;
  let footer = null;
  if (kind === "po") {
    body = (
      <>
        <label className="form-label" htmlFor="so-edit-po">Customer PO</label>
        <input id="so-edit-po" className="form-control form-control-lg" value={po} onChange={(e) => setPo(e.target.value)} autoComplete="off" />
      </>
    );
    footer = (
      <button type="button" className="btn btn-primary btn-lg w-100" disabled={saving} onClick={() => save({ customer_po: po || null }, QUICK_EDIT_FAILURE.customerPo)}>
        {saving ? "Saving…" : "Save"}
      </button>
    );
  } else if (kind === "promised") {
    body = (
      <>
        <div className="d-flex gap-2 mb-3" role="group" aria-label="Promise by">
          <button type="button" className={`btn ${mode === "lead_time" ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={mode === "lead_time"} onClick={() => setMode("lead_time")}>Lead time</button>
          <button type="button" className={`btn ${mode === "date" ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={mode === "date"} onClick={() => setMode("date")}>Specific date</button>
        </div>
        {mode === "lead_time" ? (
          <LeadTimeSelector value={leadTime} onChange={setLeadTime} />
        ) : (
          <>
            <label className="form-label" htmlFor="so-edit-promised">Promised date</label>
            <input id="so-edit-promised" type="date" className="form-control form-control-lg" value={date} onChange={(e) => setDate(e.target.value)} />
          </>
        )}
        <div className="jq-muted mt-2">Production on this order is re-dated to it (unless a target date is set).</div>
      </>
    );
    footer = (
      <button type="button" className="btn btn-primary btn-lg w-100" disabled={saving || (mode === "date" && !date)} onClick={() => save(promisedPayload(mode, leadTime, date), QUICK_EDIT_FAILURE.promised)}>
        {saving ? "Saving…" : "Save"}
      </button>
    );
  } else if (kind === "target") {
    body = (
      <>
        <label className="form-label" htmlFor="so-edit-target">Target date</label>
        <input id="so-edit-target" type="date" className="form-control form-control-lg" value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="jq-muted mt-2">Production schedules to this date; the customer's promise doesn't change. Clear it to go back to the promised date.</div>
        <button type="button" className="btn btn-warning w-100 mt-3" disabled={saving} onClick={asap}>⚡ ASAP (today)</button>
        {header?.target_completion_date && (
          <button type="button" className="btn btn-outline-danger w-100 mt-2" disabled={saving} onClick={() => save({ target_completion_date: null }, QUICK_EDIT_FAILURE.target)}>
            Clear the target
          </button>
        )}
      </>
    );
    footer = (
      <button type="button" className="btn btn-primary btn-lg w-100" disabled={saving || !date} onClick={() => save({ target_completion_date: date || null }, QUICK_EDIT_FAILURE.target)}>
        {saving ? "Saving…" : "Save"}
      </button>
    );
  }

  return (
    <FullScreenPanel open={!!kind} title={TITLES[kind] || ""} onClose={saving ? undefined : onClose} footer={footer} testId="so-edit-panel">
      {error && <div className="jq-note jq-note-danger mb-2" role="alert">{error}</div>}
      {body}
    </FullScreenPanel>
  );
}
